'use client';

import React from 'react';
import { FIG_RED, FIG_TEXT, FigureCaption } from '../figureStyle';

// ─── Config ──────────────────────────────────────────────────────────────────
// Values in millions of H100-equivalents. 1 square = SCALE M H100e.
// 2026 → 20M = 1 square; growth accelerates through 2034.

// Labels round to 1 significant figure; `value` stays exact (it sizes the
// square blocks: 489M -> 500M label but 24 squares).
const YEARS = [
  { year: 2026, value: 20,     label: '20M'  },
  { year: 2030, value: 489,    label: '500M' },
  { year: 2032, value: 3160,   label: '3B'   },
  { year: 2034, value: 58000,  label: '60B'  },
];

const SCALE = 20; // each square = 20M H100e (so 2026 is exactly 1 square)
const BOX = 4;
const GAP = 1;
const STEP = BOX + GAP;
const BLOCK_GAP = 30;

const CAPTION = 'World Compute';

// ─── Main Component ──────────────────────────────────────────────────────────

export default function ComputeGrowthChart() {
  // For each year, pick cols so the block is roughly square
  const blocks = YEARS.map((y) => {
    const squares = Math.max(1, Math.round(y.value / SCALE));
    const cols = Math.max(1, Math.round(Math.sqrt(squares)));
    const rows = Math.ceil(squares / cols);
    const width = cols * STEP - GAP;
    const height = rows * STEP - GAP;
    return { ...y, squares, cols, rows, width, height };
  });

  const maxH = Math.max(...blocks.map((b) => b.height));
  const topPad = 4;
  const baseline = topPad + maxH;
  const labelY = baseline + 18;
  const valueY = baseline + 32;

  // Compute x positions (variable width blocks)
  const leftPad = 12;
  let cx = leftPad;
  const blockPositions = blocks.map((b) => {
    const x = cx;
    cx += b.width + BLOCK_GAP;
    return x;
  });
  const svgWidth = cx - BLOCK_GAP + 12;
  const svgHeight = valueY + 10;

  return (
    <div className="max-w-[1200px] mx-auto px-12 py-16">
      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full h-auto font-['et-book',Palatino,'Palatino_Linotype','Book_Antiqua',Georgia,serif]">
        {blocks.map((block, bi) => {
          const blockX = blockPositions[bi];
          const offsetY = topPad + (maxH - block.height);

          const squares = Array.from({ length: block.squares }).map((_, i) => {
            const col = i % block.cols;
            const row = Math.floor(i / block.cols);
            return (
              <rect
                key={`${bi}-${i}`}
                x={blockX + col * STEP}
                y={offsetY + row * STEP}
                width={BOX}
                height={BOX}
                fill={FIG_RED}
                rx={0.5}
              />
            );
          });

          return (
            <g key={block.year}>
              {squares}
              <text
                x={blockX + block.width / 2}
                y={labelY}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
                fill={FIG_TEXT}
              >
                {block.year}
              </text>
              <text
                x={blockX + block.width / 2}
                y={valueY}
                textAnchor="middle"
                fontSize={11}
                fill={FIG_TEXT}
                opacity={0.65}
              >
                {block.label}
              </text>
            </g>
          );
        })}
      </svg>
      <FigureCaption title={CAPTION} />
    </div>
  );
}
